import type { FC } from 'react';

/**
 * The Analytics "Peak Usage Hours" histogram.
 *
 * Ports the prototype's `viewBox="0 0 300 140"`: 18px-wide bars on a 25px pitch, 3px corner
 * radius, baseline at y=124 and 8px axis labels at y=136. The prototype shows 12 of the 24 hours;
 * this keeps the same frame and lets the caller decide how many buckets to draw.
 */
export interface BarChartBar {
  label: string;
  value: number;
  /** Rendered under the peak bar only, as the prototype does. */
  highlight?: boolean;
}

interface BarChartProps {
  bars: BarChartBar[];
  /** Shown above the tallest bar, e.g. "Peak: 7PM". */
  peakLabel?: string;
  /** Which bars get an axis label. Others are drawn without one, as in the prototype. */
  labelEvery?: number;
}

const WIDTH = 300;
const HEIGHT = 140;
const BASELINE = 124;
const MAX_BAR_HEIGHT = 84;
const MIN_BAR_HEIGHT = 4;

const BarChart: FC<BarChartProps> = ({ bars, peakLabel, labelEvery = 3 }) => {
  const max = Math.max(1, ...bars.map((bar) => bar.value));
  const pitch = WIDTH / bars.length;
  const barWidth = Math.max(4, pitch - 7);
  const peakIndex = bars.findIndex((bar) => bar.value === max);

  return (
    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="w-full" role="img">
      {bars.map((bar, index) => {
        const height = Math.max(MIN_BAR_HEIGHT, (bar.value / max) * MAX_BAR_HEIGHT);
        const x = index * pitch + (pitch - barWidth) / 2;
        const isPeak = index === peakIndex && bar.value > 0;
        return (
          <rect
            key={bar.label}
            x={Math.round(x * 100) / 100}
            y={Math.round((BASELINE - height) * 100) / 100}
            width={Math.round(barWidth * 100) / 100}
            height={Math.round(height * 100) / 100}
            rx="3"
            // A zero bucket keeps the prototype's grey stub so the axis still reads as a day.
            fill={
              bar.value === 0
                ? 'var(--color-surface-muted-2)'
                : isPeak
                  ? 'var(--color-brand-500)'
                  : 'var(--color-brand-300)'
            }
          />
        );
      })}

      {peakLabel && peakIndex >= 0 ? (
        <text
          x={Math.min(WIDTH - 2, Math.max(2, peakIndex * pitch + pitch / 2))}
          y="35"
          textAnchor={
            peakIndex <= 1 ? 'start' : peakIndex >= bars.length - 2 ? 'end' : 'middle'
          }
          fontSize="9"
          fontWeight="700"
          fill="var(--color-brand-500)"
        >
          {peakLabel}
        </text>
      ) : null}

      {bars.map((bar, index) =>
        index % labelEvery === 0 ? (
          <text
            key={`label-${bar.label}`}
            x={index * pitch + pitch / 2}
            y="136"
            // Anchoring the outermost labels inward stops the first and last being clipped
            // by the viewBox — "12AM" was rendering as "2AM".
            textAnchor={index === 0 ? 'start' : index >= bars.length - labelEvery ? 'end' : 'middle'}
            fontSize="8"
            fill={index === peakIndex ? 'var(--color-brand-500)' : 'var(--color-ink-soft)'}
            fontWeight={index === peakIndex ? '700' : undefined}
          >
            {bar.label}
          </text>
        ) : null,
      )}
    </svg>
  );
};

export default BarChart;
